import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuthState } from '../hooks/useAuthState';
import { getEvent } from '../lib/firestore/events';
import {
  getCurrentCheckInToken,
  generateQRCode,
  generateCheckInURL,
} from '../lib/qr-checkin';
import '../styles/EventCheckInHost.css';

const EventCheckInHost = () => {
  const { eventId } = useParams();
  const { user } = useAuthState();
  const navigate = useNavigate();

  const [event, setEvent] = useState(null);
  const [qrCodeData, setQrCodeData] = useState('');
  const [checkInURL, setCheckInURL] = useState('');
  const [token, setToken] = useState('');
  const [loading, setLoading] = useState(true);
  const [isHost, setIsHost] = useState(false);

  useEffect(() => {
    if (eventId && user) {
      loadEventAndGenerateQR();
      // Refresh QR code every 30 seconds
      const interval = setInterval(() => {
        refreshQRCode();
      }, 30000);

      return () => clearInterval(interval);
    }
  }, [eventId, user]);

  const loadEventAndGenerateQR = async () => {
    if (!eventId || !user) return;

    setLoading(true);
    try {
      const eventData = await getEvent(eventId);
      setEvent(eventData);

      // Check if user is host
      const userIsHost =
        eventData.hostId === user.uid ||
        (eventData.hostType === 'group' &&
          false); // Would need to check group membership

      setIsHost(userIsHost);

      if (userIsHost) {
        await refreshQRCode();
      }
    } catch (error) {
      console.error('Error loading event:', error);
    } finally {
      setLoading(false);
    }
  };

  const refreshQRCode = async () => {
    if (!eventId) return;

    try {
      const tokenData = await getCurrentCheckInToken(eventId);
      if (tokenData) {
        setToken(tokenData.token);
        const url = generateCheckInURL(eventId, tokenData.token);
        setCheckInURL(url);

        // Generate QR code image
        const qrData = await generateQRCode(url);
        setQrCodeData(qrData);
      }
    } catch (error) {
      console.error('Error generating QR code:', error);
    }
  };

  if (loading) {
    return (
      <div className="checkin-host-container">
        <div className="loading">Loading...</div>
      </div>
    );
  }

  if (!event) {
    return (
      <div className="checkin-host-container">
        <div className="error-message">Event not found</div>
      </div>
    );
  }

  if (!isHost) {
    return (
      <div className="checkin-host-container">
        <div className="error-message">
          You must be the event host to access check-in
        </div>
        <button className="btn btn-primary" onClick={() => navigate(`/events/${eventId}`)}>
          Back to Event
        </button>
      </div>
    );
  }

  return (
    <div className="checkin-host-container">
      <div className="checkin-host-card">
        <header className="checkin-host-header">
          <button className="btn-back" onClick={() => navigate(`/events/${eventId}`)}>
            ← Back to Event
          </button>
          <h1>Check-In QR Code</h1>
          <p>{event.title}</p>
        </header>

        <div className="qr-display-section">
          {qrCodeData ? (
            <>
              <div className="qr-code-wrapper">
                <img src={qrCodeData} alt="Check-in QR Code" className="qr-code-image" />
              </div>
              <p className="qr-hint">
                Display this QR code at the event entrance. Guests scan to check in.
                <br />
                <strong>QR code rotates every 60 seconds for security.</strong>
              </p>
              <button
                className="btn btn-secondary"
                onClick={refreshQRCode}
              >
                Refresh QR Code
              </button>
            </>
          ) : (
            <div className="loading">Generating QR code...</div>
          )}
        </div>

        <div className="checkin-link-section">
          <h3>Share Check-In Link</h3>
          <div className="link-display">
            <input
              type="text"
              readOnly
              value={checkInURL}
              className="link-input"
            />
            <button
              className="btn btn-secondary"
              onClick={async () => {
                try {
                  await navigator.clipboard.writeText(checkInURL);
                  alert('Link copied to clipboard!');
                } catch (error) {
                  console.error('Failed to copy:', error);
                }
              }}
            >
              Copy
            </button>
          </div>
          <p className="link-hint">
            Share this link if QR code scanning isn't available
          </p>
        </div>
      </div>
    </div>
  );
};

export default EventCheckInHost;




